import { useCallback, useMemo, useRef, useState } from "react";

import AddRounded from "@mui/icons-material/AddRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import AccountBalanceOutlined from "@mui/icons-material/AccountBalanceOutlined";
import ConfirmationNumberOutlined from "@mui/icons-material/ConfirmationNumberOutlined";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import LockResetOutlined from "@mui/icons-material/LockResetOutlined";
import LogoutOutlined from "@mui/icons-material/LogoutOutlined";
import PersonOutlineOutlined from "@mui/icons-material/PersonOutlineOutlined";
import PushPinOutlined from "@mui/icons-material/PushPinOutlined";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { useLocation, useNavigate } from "react-router-dom";

import LendmarqLogo from "../assets/LendmarqLogo";
import tpoColors, { status as tpoStatus } from "../tpo/theme/colors";
import { level1, teal } from "../theme/colors";
import {
  SEND_MENU_HEADING,
  SEND_TO_BORROWER_ITEMS,
} from "./sendToBorrowerMenu";
import Flx from "../components/layout/Flx";
import NotificationsBell from "../components/notifications/NotificationsBell";
import ReduxStateDevButton from "../components/dev/ReduxStateDevButton";
import LoanSearchModal from "../components/modals/common/LoanSearchModal";
import SizerImportModal from "../components/pipelineTable/buttons/SizerImportModal";
import Txt from "../components/typography/Txt";
import ChangePasswordDialog from "./ChangePasswordDialog";
import NewTicketModal from "../screens/SupportScreen/components/NewTicketModal";

// Brand header — Lendmarq blue (#1F5AF2). Lendmarq divergence (CLAUDE.md #2/#10):
// hardcoded blue, NOT Eastview's onHeader teal tokens.
const HEADER_GRADIENT = "linear-gradient(180deg, #3D72FF, #1F5AF2)"; // brand header (Lendmarq blue)
const HEADER_BG = "#1F5AF2"; // solid brand color (white-button text / avatar)
const HEADER_FG = "#ffffff"; // white tabs / icons

// One item in the nav-tab hover menu and its sub-tab flyout.
const NAV_MENU_ITEM_SX = {
  px: 1.5,
  py: 1.1,
  borderRadius: "8px",
  fontSize: 14,
  fontWeight: 500,
  color: "#1b2a30",
  minHeight: 0,
  "&:not(:last-of-type)": { mb: 0.25 },
  "&:hover": { backgroundColor: "#f4f7f8" },
  "&.Mui-selected": {
    color: level1.text,
    backgroundColor: level1.tint,
    "&:hover": { backgroundColor: teal[200] },
  },
};
const HEADER_HEIGHT = 64;

const getInitials = (user) => {
  const source = user?.fullName || user?.emailAddress || "";
  const parts = source.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// MUI Tabs styled so the indicator sits flush at the BOTTOM of the header.
// Mirrors broker_portal_frontend/src/AuthenticatedDashboard/TopBar.js:
// the Tabs stretch full toolbar height (alignSelf:stretch + flexContainer
// height:100% align stretch), so the bottom-anchored indicator lands at the
// very bottom edge of the header.
const tabsSx = {
  alignSelf: "stretch",
  minHeight: 0,
  "& .MuiTabs-indicator": {
    height: 2.5,
    borderRadius: "2px 2px 0 0",
    backgroundColor: HEADER_FG,
  },
  "& .MuiTabs-scrollButtons": { color: HEADER_FG },
  "& .MuiTabs-scrollButtons.Mui-disabled": { opacity: 0.3 },
  "& .MuiTabs-flexContainer": {
    gap: 0.5,
    alignItems: "stretch",
    height: "100%",
    // Stretch to fill the (flex:1) Tabs root so a tab flagged `pushRight` can
    // float to the far right via marginLeft:auto.
    width: "100%",
  },
};

const tabSx = {
  textTransform: "none",
  // fontSize: 13.5,
  fontWeight: 400,
  fontSize: 12,
  // fontSize: 14,
  // fontWeight: 500,
  letterSpacing: "0.01em",
  minHeight: 0,
  // MUI forces 90px min on sm+; let each tab hug its own label instead.
  minWidth: "0 !important",
  px: 1.5,
  whiteSpace: "nowrap",
  color: "rgba(255,255,255,0.78)",
  transition: "color 0.15s",
  "&:hover": { color: HEADER_FG },
  "&.Mui-selected": {
    color: HEADER_FG,
    fontWeight: 700,
    background: "linear-gradient(0deg, #ffffff1c, #ffffff0d)",
    // background: "#ffffff1c",
  },
};

// ============================================================
// TopHeader — top app bar
// ============================================================
const TopHeader = ({
  navItems = [],
  user,
  onLogout,
  pinnedCount = 0,
  pinnedOpen = false,
  onTogglePinned,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [userAnchor, setUserAnchor] = useState(null);
  // "New Loan" dropdown menu (Create New Loan / Create Loan from Sizer) + the
  // sizer-import modal it opens.
  const [newLoanAnchor, setNewLoanAnchor] = useState(null);
  const [sendAnchor, setSendAnchor] = useState(null);
  const [sizerImportOpen, setSizerImportOpen] = useState(false);
  // Nav-tab dropdown (e.g. Closing → Pipeline / Calendar). Opens on hover; the
  // tab itself still navigates to its default route on click. A short close
  // delay lets the cursor cross the gap from the tab into the menu.
  const [navMenuAnchor, setNavMenuAnchor] = useState(null);
  const [navMenuItems, setNavMenuItems] = useState([]);
  const navMenuCloseTimer = useRef(null);
  const cancelNavMenuClose = useCallback(() => {
    if (navMenuCloseTimer.current) {
      clearTimeout(navMenuCloseTimer.current);
      navMenuCloseTimer.current = null;
    }
  }, []);
  const openNavMenu = useCallback(
    (anchorEl, items) => {
      cancelNavMenuClose();
      setNavMenuAnchor(anchorEl);
      setNavMenuItems(items || []);
    },
    [cancelNavMenuClose],
  );
  // Second level: an entry that carries `subTabs` (Admin → Pricing → DSCR
  // Pricing / RTL & GUC Pricing / Pricing Config) opens a flyout to its right on
  // hover. It shares the close timer above, so moving between the two papers
  // keeps both open and leaving either closes both.
  const [navSubAnchor, setNavSubAnchor] = useState(null);
  const [navSubItems, setNavSubItems] = useState([]);
  const openNavSubMenu = useCallback((anchorEl, items) => {
    setNavSubAnchor(anchorEl);
    setNavSubItems(items || []);
  }, []);
  const closeNavMenu = useCallback(() => {
    cancelNavMenuClose();
    setNavMenuAnchor(null);
    setNavSubAnchor(null);
  }, [cancelNavMenuClose]);
  const scheduleNavMenuClose = useCallback(() => {
    cancelNavMenuClose();
    navMenuCloseTimer.current = setTimeout(() => {
      setNavMenuAnchor(null);
      setNavSubAnchor(null);
    }, 160);
  }, [cancelNavMenuClose]);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const openSupport = useCallback(() => {
    setSupportOpen(true);
  }, []);

  const initials = useMemo(() => getInitials(user), [user]);

  // Active tab = the item whose `to` (or any of its dropdown sub-routes) is a
  // prefix of the current path — so e.g. Support stays lit on /changelog.
  const currentValue = useMemo(() => {
    const startsWith = (to) => to && location.pathname.startsWith(to);
    const match = navItems.find(
      (i) => startsWith(i.to) || (i.menu || []).some((m) => startsWith(m.to)),
    );
    return match ? match.to : false;
  }, [navItems, location.pathname]);

  const handleLogout = useCallback(() => {
    setUserAnchor(null);
    onLogout?.();
  }, [onLogout]);

  return (
    <>
      <Box
        component="header"
        sx={{
          flexShrink: 0,
          background: HEADER_GRADIENT,
          color: HEADER_FG,
          boxShadow:
            "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.08) 0px 1px 2px",
          zIndex: 1100,
          position: "relative",
        }}
      >
        <Flx
          ac
          sx={{
            height: `${HEADER_HEIGHT}px`,
            // px: 3,
            pr: 3,
            pl: 0,
            gap: 3,
            width: "100%",
          }}
        >
          {/* Logo — Lendmarq wordmark (white, legible on the brand header) —
              followed by loan search and the pinned-tabs toggle, which sit with
              the logo so they read as chrome for the tab strip beneath it. */}
          <Flx
            ac
            g={1.5}
            sx={{
              flexShrink: 0,
              pl: 3,
              pr: 1,
            }}
          >
            <LendmarqLogo height={24} fill={HEADER_FG} />
            <Flx ac g={0.5} sx={{ flexShrink: 0 }}>
              <Tooltip title="Search loans" arrow>
                <IconButton
                  onClick={() => setSearchOpen(true)}
                  size="small"
                  sx={{
                    color: HEADER_FG,
                    "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
                  }}
                >
                  <SearchOutlined />
                </IconButton>
              </Tooltip>
              {pinnedCount > 0 && (
                <Tooltip
                  title={pinnedOpen ? "Hide pinned tabs" : "Show pinned tabs"}
                  arrow
                >
                  <IconButton
                    onClick={onTogglePinned}
                    size="small"
                    sx={{
                      color: HEADER_FG,
                      bgcolor: pinnedOpen
                        ? "rgba(255,255,255,0.2)"
                        : "transparent",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
                    }}
                  >
                    <Badge
                      badgeContent={pinnedCount}
                      sx={{
                        "& .MuiBadge-badge": {
                          // White fill + brand-blue numeral for strong contrast
                          // against the blue header (was slate "secondary",
                          // which blended into the header background).
                          bgcolor: "#ffffff",
                          color: HEADER_BG,
                          fontSize: "9px",
                          fontWeight: 700,
                          height: 15,
                          minWidth: 15,
                          px: "4px",
                          border: "1px solid rgba(0,0,0,0.08)",
                        },
                      }}
                    >
                      <PushPinOutlined />
                    </Badge>
                  </IconButton>
                </Tooltip>
              )}
            </Flx>
          </Flx>

          {/* Left tabs — grow to fill; underline indicator at header bottom */}
          <Tabs
            value={currentValue}
            onChange={(_, v) => navigate(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ ...tabsSx, flex: 1, minWidth: 0 }}
          >
            {navItems.map((item) => (
              <Tab
                key={item.to}
                value={item.to}
                // For dropdown tabs, a plain body click still lands on the
                // default route even when the tab is already "selected" (where
                // Tabs' onChange would otherwise no-op). The menu opens on hover.
                onClick={item.menu ? () => navigate(item.to) : undefined}
                onMouseEnter={
                  item.menu
                    ? (e) => openNavMenu(e.currentTarget, item.menu)
                    : undefined
                }
                onMouseLeave={item.menu ? scheduleNavMenuClose : undefined}
                label={
                  item.menu ? (
                    <Flx ac g={0.2}>
                      {item.label}
                      <KeyboardArrowDownRounded
                        sx={{ fontSize: 16, opacity: 0.85 }}
                      />
                    </Flx>
                  ) : (
                    item.label
                  )
                }
                disableRipple
                sx={item.pushRight ? { ...tabSx, marginLeft: "auto" } : tabSx}
              />
            ))}
          </Tabs>

          {/* Nav-tab dropdown (Closing → Pipeline / Calendar) */}
          <Menu
            anchorEl={navMenuAnchor}
            open={Boolean(navMenuAnchor)}
            onClose={closeNavMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            // Hover menu: no backdrop, and let pointer events pass through the
            // modal root so hovering the tab/menu isn't blocked. The paper
            // re-enables pointer events and keeps the menu open while hovered.
            hideBackdrop
            disableScrollLock
            disableAutoFocusItem
            sx={{ pointerEvents: "none" }}
            // Zero the default MenuList padding so the paper's own padding wraps
            // the rounded items evenly (the soft-card look from the reference).
            MenuListProps={{ sx: { py: 0 } }}
            slotProps={{
              paper: {
                onMouseEnter: cancelNavMenuClose,
                onMouseLeave: scheduleNavMenuClose,
                elevation: 0,
                sx: {
                  minWidth: 168,
                  mt: 0.25,
                  p: 0.75,
                  pointerEvents: "auto",
                  borderRadius: "12px",
                  border: "1px solid #e9edef",
                  boxShadow: "0 14px 38px rgba(20,40,46,0.16)",
                  overflow: "visible",
                  // Small pointer arrow at the top, near the left edge (matches
                  // the tab the menu drops from). A rotated square showing only
                  // its top-left borders so it reads as a notch on the paper.
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: -6,
                    left: 22,
                    width: 12,
                    height: 12,
                    backgroundColor: "#fff",
                    borderLeft: "1px solid #e9edef",
                    borderTop: "1px solid #e9edef",
                    borderTopLeftRadius: "3px",
                    transform: "rotate(45deg)",
                  },
                },
              },
            }}
          >
            {navMenuItems.map((sub) => {
              // Exact-ish active match so a parent route isn't highlighted on a
              // deeper sub-route that shares its prefix. "/loans" (Full Pipeline)
              // and "/loans/closing" (Pipeline) are prefixes of their sibling
              // buckets/calendar, so they match exactly only.
              const EXACT_ONLY = ["/loans", "/loans/closing"];
              const selected =
                location.pathname === sub.to ||
                (!EXACT_ONLY.includes(sub.to) &&
                  location.pathname.startsWith(sub.to));
              const hasSub = !!sub.subTabs?.length;
              const subOpen = hasSub && navSubItems === sub.subTabs && !!navSubAnchor;
              return (
                <MenuItem
                  key={sub.to}
                  selected={selected}
                  onClick={() => {
                    closeNavMenu();
                    navigate(sub.to);
                  }}
                  // Hovering an entry with sub-tabs opens its flyout; any other
                  // entry closes it.
                  onMouseEnter={(e) =>
                    hasSub
                      ? openNavSubMenu(e.currentTarget, sub.subTabs)
                      : setNavSubAnchor(null)
                  }
                  aria-haspopup={hasSub ? "menu" : undefined}
                  aria-expanded={hasSub ? subOpen : undefined}
                  disableRipple
                  sx={{
                    ...NAV_MENU_ITEM_SX,
                    ...(subOpen && !selected
                      ? { backgroundColor: "#f4f7f8" }
                      : null),
                  }}
                >
                  <Box component="span" sx={{ flex: 1 }}>
                    {sub.label}
                  </Box>
                  {hasSub ? (
                    <ChevronRightRounded
                      sx={{ fontSize: 18, ml: 1.5, mr: -0.5, opacity: 0.55 }}
                    />
                  ) : null}
                </MenuItem>
              );
            })}
          </Menu>

          {/* Second-level flyout for an entry's sub-tabs (Admin → Pricing →
              DSCR Pricing / RTL & GUC Pricing / Pricing Config). Opens to the
              right of the hovered entry; same paper as the menu above, minus
              the notch. */}
          <Menu
            anchorEl={navSubAnchor}
            open={Boolean(navMenuAnchor) && Boolean(navSubAnchor)}
            onClose={closeNavMenu}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            hideBackdrop
            disableScrollLock
            disableAutoFocus
            disableAutoFocusItem
            disableEnforceFocus
            disableRestoreFocus
            sx={{ pointerEvents: "none" }}
            MenuListProps={{ sx: { py: 0 } }}
            slotProps={{
              paper: {
                onMouseEnter: cancelNavMenuClose,
                onMouseLeave: scheduleNavMenuClose,
                elevation: 0,
                sx: {
                  minWidth: 180,
                  // Line the first item up with the hovered entry: the paper
                  // padding (6px) sits above the entry's own top edge.
                  mt: -0.75,
                  ml: 1,
                  p: 0.75,
                  pointerEvents: "auto",
                  borderRadius: "12px",
                  border: "1px solid #e9edef",
                  boxShadow: "0 14px 38px rgba(20,40,46,0.16)",
                },
              },
            }}
          >
            {navSubItems.map((tab) => (
              <MenuItem
                key={tab.to}
                selected={location.pathname.startsWith(tab.to)}
                onClick={() => {
                  closeNavMenu();
                  navigate(tab.to);
                }}
                disableRipple
                sx={NAV_MENU_ITEM_SX}
              >
                {tab.label}
              </MenuItem>
            ))}
          </Menu>

          {/* Right cluster: Send · Create Loan · | · support · notifications · avatar */}
          <Flx ac g={1} sx={{ flexShrink: 0 }}>
            {/* Send — mints a borrower-facing link. Click-to-open rather than
                the hover behaviour the nav tabs use: this menu is an action
                picker, not a route group, so it should not appear from a
                passing cursor. */}
            <Button
              variant="outlined"
              size="small"
              startIcon={
                <SendOutlinedIcon sx={{ transform: "rotate(-45deg)" }} />
              }
              endIcon={
                <KeyboardArrowDownRounded
                  sx={{
                    fontSize: 18,
                    ml: -0.5,
                    transition: "transform 0.15s",
                    transform: sendAnchor ? "rotate(180deg)" : "none",
                  }}
                />
              }
              onClick={(e) => setSendAnchor(e.currentTarget)}
              aria-haspopup="true"
              aria-expanded={Boolean(sendAnchor)}
              sx={{
                flexShrink: 0,
                borderRadius: 999,
                px: 2,
                py: 0.6,
                fontWeight: 600,
                whiteSpace: "nowrap",
                color: HEADER_FG,
                borderColor: alpha(HEADER_FG, 0.3),
                "&:hover": {
                  borderColor: alpha(HEADER_FG, 0.55),
                  backgroundColor: alpha(HEADER_FG, 0.08),
                },
              }}
            >
              Send
            </Button>

            <Menu
              anchorEl={sendAnchor}
              open={Boolean(sendAnchor)}
              onClose={() => setSendAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              disableScrollLock
              MenuListProps={{ sx: { py: 0 } }}
              slotProps={{
                paper: {
                  elevation: 0,
                  sx: {
                    mt: 1,
                    width: 372,
                    maxWidth: "calc(100vw - 32px)",
                    p: 1,
                    borderRadius: "16px",
                    border: `1px solid ${tpoColors.slate200}`,
                    boxShadow: "0 18px 44px rgba(20,40,46,0.18)",
                  },
                },
              }}
            >
              <Box
                sx={{
                  px: 1.5,
                  pt: 1,
                  pb: 0.75,
                  fontSize: 11.5,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: tpoColors.slate400,
                }}
              >
                {SEND_MENU_HEADING}
              </Box>
              {SEND_TO_BORROWER_ITEMS.map((item) => {
                const Icon = item.icon;
                const tint = tpoStatus[item.tone] || tpoStatus.neutral;
                return (
                  <MenuItem
                    key={item.to}
                    onClick={() => {
                      setSendAnchor(null);
                      navigate(item.to);
                    }}
                    disableRipple
                    // whiteSpace: normal lets the description wrap — MenuItem
                    // forces nowrap by default and would clip it to one line.
                    sx={{
                      alignItems: "flex-start",
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: "12px",
                      whiteSpace: "normal",
                      "&:hover": { backgroundColor: tpoColors.slate50 },
                    }}
                  >
                    <Flx
                      ac
                      jc="center"
                      sx={{
                        flexShrink: 0,
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        backgroundColor: tint.bg,
                      }}
                    >
                      {/* ListItemIcon-adjacent slots override icon color at a
                          higher specificity than a plain sx; force it. */}
                      <Icon
                        sx={{
                          fontSize: "20px !important",
                          color: `${tint.fg} !important`,
                        }}
                      />
                    </Flx>
                    <Flx column g={0.25} sx={{ minWidth: 0 }}>
                      <Box
                        sx={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: tpoColors.slate900,
                        }}
                      >
                        {item.label}
                      </Box>
                      <Box
                        sx={{
                          fontSize: 13.5,
                          lineHeight: 1.4,
                          color: tpoColors.slate500,
                        }}
                      >
                        {item.description}
                      </Box>
                    </Flx>
                  </MenuItem>
                );
              })}
            </Menu>

            <Button
              variant="contained"
              size="small"
              startIcon={<AddRounded />}
              endIcon={<KeyboardArrowDownRounded sx={{ ml: -0.5 }} />}
              onClick={(e) => setNewLoanAnchor(e.currentTarget)}
              sx={{
                flexShrink: 0,
                borderRadius: 999,
                px: 2,
                py: 0.6,
                fontWeight: 600,
                whiteSpace: "nowrap",
                bgcolor: "#ffffff",
                color: HEADER_BG,
                boxShadow: "0 1px 3px rgba(15,23,42,0.18)",
                "&:hover": {
                  bgcolor: "#eef6f8",
                  boxShadow: "0 3px 8px rgba(15,23,42,0.24)",
                },
              }}
            >
              New Loan
            </Button>
            <Menu
              anchorEl={newLoanAnchor}
              open={Boolean(newLoanAnchor)}
              onClose={() => setNewLoanAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{ paper: { sx: { mt: 0.5, minWidth: 220 } } }}
            >
              <MenuItem
                onClick={() => {
                  setNewLoanAnchor(null);
                  navigate("/loan/create");
                }}
                sx={{ fontSize: 13, gap: 1 }}
              >
                <ListItemIcon sx={{ minWidth: "auto !important" }}>
                  <AddRounded sx={{ fontSize: 18 }} />
                </ListItemIcon>
                Create New Loan
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setNewLoanAnchor(null);
                  navigate("/acquisition/create");
                }}
                sx={{ fontSize: 13, gap: 1 }}
              >
                <ListItemIcon sx={{ minWidth: "auto !important" }}>
                  <AccountBalanceOutlined sx={{ fontSize: 18 }} />
                </ListItemIcon>
                Create Acquisition Loan
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setNewLoanAnchor(null);
                  setSizerImportOpen(true);
                }}
                sx={{ fontSize: 13, gap: 1 }}
              >
                <ListItemIcon sx={{ minWidth: "auto !important" }}>
                  <UploadFileOutlined sx={{ fontSize: 18 }} />
                </ListItemIcon>
                Create Loan from Sizer
              </MenuItem>
            </Menu>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ my: 1.5, ml: 1, borderColor: "rgba(255,255,255,0.3)" }}
            />
            <Flx ac g={0.2} sx={{ flexShrink: 0 }}>
              {/* Dev/localhost only: JSON preview of the entire Redux store. */}
              <ReduxStateDevButton iconColor={HEADER_FG} />

              <Tooltip title="Submit a support ticket" arrow>
                <IconButton
                  onClick={openSupport}
                  size="small"
                  sx={{ color: HEADER_FG }}
                >
                  <ConfirmationNumberOutlined />
                </IconButton>
              </Tooltip>

              <NotificationsBell iconColor={HEADER_FG} />

              <Tooltip title="Account" arrow>
                <IconButton
                  onClick={(e) => setUserAnchor(e.currentTarget)}
                  sx={{ p: 0.5 }}
                  size="small"
                >
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      fontSize: "14px",
                      fontWeight: 600,
                      bgcolor: "#ffffff",
                      color: HEADER_BG,
                    }}
                  >
                    {initials}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Flx>
          </Flx>
        </Flx>

        {/* Hover-reveal handle to re-open the pinned tabs when they're hidden.
            A subtle nub straddles the header's bottom edge; hovering it surfaces
            a glowing pill that toggles the pinned-tabs strip back open. Only
            shown when tabs are hidden AND there are pinned tabs to show. */}
        {!pinnedOpen && pinnedCount > 0 && (
          <Box
            onClick={onTogglePinned}
            role="button"
            tabIndex={0}
            aria-label={`Show ${pinnedCount} pinned tab${
              pinnedCount > 1 ? "s" : ""
            }`}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onTogglePinned?.();
              }
            }}
            sx={{
              position: "absolute",
              bottom: 0,
              left: "50%",
              transform: "translate(-50%, 50%)", // straddle the bottom edge
              zIndex: 1101,
              width: 168,
              height: 12,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              // Resting hint: a small centered nub. Hidden on hover as the pill
              // takes over.
              "& .pin-nub": {
                width: 44,
                height: 4,
                borderRadius: 999,
                background: "rgba(255,255,255,0.55)",
                transition: "opacity .16s ease",
              },
              // The reveal pill: hidden until the zone is hovered/focused, then
              // it drops just below the header with a glow.
              "& .pin-pill": {
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -2px)",
                opacity: 0,
                pointerEvents: "none",
                transition: "opacity .18s ease, transform .18s ease",
                whiteSpace: "nowrap",
              },
              "&:hover .pin-nub, &:focus-visible .pin-nub": { opacity: 0 },
              "&:hover .pin-pill, &:focus-visible .pin-pill": {
                opacity: 1,
                transform: "translate(-50%, 6px)",
                pointerEvents: "auto",
              },
            }}
          >
            <Box className="pin-nub" />
            <Flx
              ac
              g={0.6}
              className="pin-pill"
              sx={{
                px: 1.25,
                py: 0.5,
                borderRadius: 999,
                background: HEADER_BG,
                color: HEADER_FG,
                border: "1px solid rgba(255,255,255,0.25)",
                boxShadow:
                  "0 8px 22px rgba(31,90,242,0.6), 0 0 14px rgba(31,90,242,0.55)",
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              <PushPinOutlined sx={{ fontSize: 13 }} />
              Show pinned tabs
              <Box
                component="span"
                sx={{
                  px: 0.6,
                  borderRadius: 999,
                  bgcolor: "#fff",
                  color: HEADER_BG,
                  fontSize: 9,
                  fontWeight: 700,
                  lineHeight: "15px",
                }}
              >
                {pinnedCount}
              </Box>
              <KeyboardArrowDownRounded sx={{ fontSize: 14 }} />
            </Flx>
          </Box>
        )}
      </Box>

      {/* Account dropdown */}
      <Menu
        anchorEl={userAnchor}
        open={Boolean(userAnchor)}
        onClose={() => setUserAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 220, mt: 1 } } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Txt sx={{ fontSize: "13px", fontWeight: 600 }}>
            {user?.fullName || "Account"}
          </Txt>
          {user?.emailAddress && (
            <Txt sx={{ fontSize: "11px", color: "text.secondary" }}>
              {user.emailAddress}
            </Txt>
          )}
        </Box>
        <Divider />
        <MenuItem
          onClick={() => {
            setUserAnchor(null);
            navigate("/profile");
          }}
          sx={{ fontSize: "13px" }}
        >
          <ListItemIcon>
            <PersonOutlineOutlined fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem
          onClick={() => {
            setUserAnchor(null);
            setChangePasswordOpen(true);
          }}
          sx={{ fontSize: "13px" }}
        >
          <ListItemIcon>
            <LockResetOutlined fontSize="small" />
          </ListItemIcon>
          Change Password
        </MenuItem>
        <MenuItem onClick={handleLogout} sx={{ fontSize: "13px" }}>
          <ListItemIcon>
            <LogoutOutlined
              fontSize="small"
              sx={{ transform: "rotate(180deg)" }}
            />
          </ListItemIcon>
          Log Out
        </MenuItem>
      </Menu>

      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />

      <NewTicketModal
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        relatedRoute={location.pathname}
      />

      {/* Global loan search (mirrors the loan drilldown search UX) */}
      <LoanSearchModal show={searchOpen} onClose={() => setSearchOpen(false)} />

      <SizerImportModal
        show={sizerImportOpen}
        onClose={() => setSizerImportOpen(false)}
      />
    </>
  );
};

export default TopHeader;
